import React from 'react';
import { TrendingUp, TrendingDown, Minus, BarChart3 } from 'lucide-react';
import type { BenchmarkComparison } from '../../types/essReference';

interface SectorBenchmarkProps {
  comparisons: BenchmarkComparison[];
  category: string;
}

export const SectorBenchmark: React.FC<SectorBenchmarkProps> = ({
  comparisons,
  category
}) => {
  const getPositionColor = (position: string) => {
    switch (position) {
      case 'above':
        return 'text-green-600 bg-green-50';
      case 'below':
        return 'text-orange-600 bg-orange-50';
      default:
        return 'text-blue-600 bg-blue-50';
    }
  };

  const getPositionIcon = (position: string) => {
    switch (position) {
      case 'above':
        return <TrendingUp className="w-4 h-4" />;
      case 'below':
        return <TrendingDown className="w-4 h-4" />;
      default:
        return <Minus className="w-4 h-4" />;
    }
  };

  const getPositionText = (position: string) => {
    switch (position) {
      case 'above':
        return 'Au-dessus de la moyenne';
      case 'below':
        return 'En-dessous de la moyenne';
      default:
        return 'Dans la moyenne';
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatPercent = (value: number) => {
    return `${value.toFixed(1)}%`;
  };

  if (comparisons.length === 0) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <div className="flex items-center space-x-2 mb-4">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Benchmark Sectoriel</h3>
        </div>
        <p className="text-gray-600 text-sm">
          Aucune donnée de benchmark disponible pour la catégorie "{category}".
        </p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-2">
          <BarChart3 className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-semibold text-gray-900">Benchmark Sectoriel</h3>
        </div>
        <span className="text-sm text-gray-500">Secteur: {category}</span>
      </div>

      <div className="space-y-4">
        {comparisons.map((comparison, index) => {
          const isCurrency = comparison.metric.includes('Affaires') || comparison.metric.includes('Investissement');
          const isPercent = comparison.metric.includes('%');

          return (
            <div key={index} className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-medium text-gray-900">{comparison.metric}</h4>
                  <p className="text-sm text-gray-600 mt-1">
                    {isCurrency
                      ? formatCurrency(comparison.user_value)
                      : isPercent
                        ? formatPercent(comparison.user_value)
                        : comparison.user_value.toFixed(0)}
                  </p>
                </div>
                <div className={`flex items-center space-x-1 px-3 py-1 rounded-full ${getPositionColor(comparison.position)}`}>
                  {getPositionIcon(comparison.position)}
                  <span className="text-xs font-medium">
                    {getPositionText(comparison.position)}
                  </span>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-600">Moyenne sectorielle:</span>
                  <span className="font-medium text-gray-900">
                    {isCurrency
                      ? formatCurrency(comparison.sector_avg)
                      : isPercent
                        ? formatPercent(comparison.sector_avg)
                        : comparison.sector_avg.toFixed(0)}
                  </span>
                </div>

                {comparison.sector_min > 0 && comparison.sector_max > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Fourchette sectorielle:</span>
                    <span className="font-medium text-gray-700">
                      {isCurrency
                        ? `${formatCurrency(comparison.sector_min)} - ${formatCurrency(comparison.sector_max)}`
                        : isPercent
                          ? `${formatPercent(comparison.sector_min)} - ${formatPercent(comparison.sector_max)}`
                          : `${comparison.sector_min.toFixed(0)} - ${comparison.sector_max.toFixed(0)}`}
                    </span>
                  </div>
                )}

                <div className="relative pt-2">
                  <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all ${comparison.position === 'above'
                          ? 'bg-green-500'
                          : comparison.position === 'below'
                            ? 'bg-orange-500'
                            : 'bg-blue-500'
                        }`}
                      style={{
                        width: `${Math.min(
                          100,
                          Math.max(
                            10,
                            (comparison.user_value / comparison.sector_avg) * 50
                          )
                        )}%`
                      }}
                    />
                  </div>
                  {comparison.deviation_pct !== 0 && (
                    <p className="text-xs text-gray-500 mt-1">
                      {comparison.deviation_pct > 0 ? '+' : ''}
                      {comparison.deviation_pct.toFixed(1)}% par rapport à la moyenne
                    </p>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-4 p-3 bg-blue-50 rounded-lg">
        <p className="text-sm text-blue-800">
          💡 Ces benchmarks sont basés sur des projets ESS réels similaires au vôtre.
          Utilisez-les comme référence pour ajuster vos objectifs.
        </p>
      </div>
    </div>
  );
};
